import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Instituciones } from '../../../shared/catalogos';
import { getAnonymousResultPeriods, getAnonymousResults, getAnonymousResultsCross, getQuestionnaireById } from './query';

/** Un periodo (allocation) listable en el selector */
export interface AnonymousResultPeriod {
  allocationId: number;
  questionnaireId: number;
  questionnaireName: string;
  status: string;      // "open" | "closed"
  startDate: string;   // "YYYY-MM-DD"
  dueDate: string;     // "YYYY-MM-DD"
  totalResponses: number;
  sentSurveysCount: number | null;   // encuestas enviadas (captura manual); null si no se capturó
}

/** Valor de un grupo demográfico */
export interface DemographicValue {
  value: string;
  count: number;             // nº de ENCUESTADOS con ese valor
  averageScore: number | null;
}

/** Cruce demográfico (por key dinámica: "gender", "age-range", ...) */
export interface DemographicBreakdown {
  key: string;
  label: string;             // fallback del back (el front resuelve el legible)
  values: DemographicValue[];
}

/** Recorte por eje demográfico. Entre filtros es AND; entre values es OR */
export interface FiltroDemografico {
  key: string;
  values: string[];
}

/** Categoría (viene plana; el árbol se arma con parentCategoryName) */
export interface AnonymousResultCategory {
  categoryName: string;
  parentCategoryName: string | null;
  averageScore: number | null;
  demographics: DemographicBreakdown[];
}

/** Resultado completo de un periodo */
export interface AnonymousResults {
  allocationId: number;
  questionnaireId: number;
  questionnaireName: string;
  status: string;              // "open" | "closed"
  fromCache: boolean;
  computedAt: string;          // ISO
  totalResponses: number;
  overallAverageScore: number | null;
  demographics: DemographicBreakdown[];   // cortes globales (nivel raíz)
  categories: AnonymousResultCategory[];
}

/** Celda interior del cruce (valueA ∧ valueB) */
export interface CrossCell {
  valueA: string;
  valueB: string;
  count: number;               // nº de ENCUESTADOS en la combinación
  averageScore: number | null; // null si no hay respuestas puntuables
}

/** Marginal del cruce (borde ya ponderado: GLOBAL por valueA o PROMEDIO por valueB) */
export interface CrossMarginal {
  value: string;
  count: number;
  averageScore: number | null;
}

/**
 * Cruce de una dimensión entre los dos ejes. En el cruce las categorías vienen
 * YA AGREGADAS a nivel dimensión padre (categoryName = "Liderazgo", etc. y
 * parentCategoryName = null); no hay que agregar hijas.
 */
export interface CrossCategory {
  categoryName: string;
  parentCategoryName: string | null;
  overallCount: number;               // esquina
  overallAverageScore: number | null; // esquina
  cells: CrossCell[];                  // interior (matriz dispersa)
  marginalsA: CrossMarginal[];         // columna GLOBAL (por valueA)
  marginalsB: CrossMarginal[];         // fila PROMEDIO (por valueB)
}

/** Resultado del cruce de dos ejes demográficos para un periodo */
export interface AnonymousResultsCross {
  allocationId: number;
  keyA: string;
  keyB: string;
  labelA: string;              // etiqueta legible del eje A (filas)
  labelB: string;              // etiqueta legible del eje B (columnas)
  totalResponses: number;
  categories: CrossCategory[]; // una por dimensión (padre)
}

/** Pregunta (hoja del catálogo) */
export interface QuestionnaireQuestion {
  id: string;
  order: number;
  description: string;
}

/** Categoría plana del cuestionario; el árbol se arma con parent */
export interface QuestionnaireCategory {
  id: string;
  name: string;
  description: string;
  parent: { id: string } | null;
  questions: QuestionnaireQuestion[];
}

/** Estructura del cuestionario para el catálogo de dimensiones */
export interface Questionnaire {
  id: string;
  name: string;
  categories: QuestionnaireCategory[];
}

@Injectable({
  providedIn: 'root'
})
export class ResultadosClimaService {

  /** Cliente Apollo nombrado del backend de encuestas (POST /api/gql) */
  private apiURL = environment.api_cuestionarios;

  /** Escala en la que el back siempre regresa los scores de clima laboral */
  private static readonly ESCALA_BACK = 10;
  /** Máximo real cuando la institución no está en Instituciones o no define maximoClimaLaboral */
  private static readonly MAXIMO_FALLBACK = 5;

  constructor(private readonly apollo: Apollo) { }

  /** Máximo real de la institución (Instituciones.maximoClimaLaboral), o el fallback si no está definido */
  public maximoEscala(institutionId: string): number {
    const inst = Instituciones.find(i => i.id === institutionId);
    return (inst?.maximoClimaLaboral as number | undefined) ?? ResultadosClimaService.MAXIMO_FALLBACK;
  }

  /**
   * Universo de un periodo: las encuestas enviadas capturadas en la asignación.
   * Si no se capturó (null) o la captura quedó por debajo de los que ya
   * contestaron, se cae a totalResponses para no reportar participación > 100%.
   */
  public universoDe(periodo: AnonymousResultPeriod | null | undefined, totalResponses: number): number {
    const enviadas = periodo?.sentSurveysCount;
    return enviadas != null && enviadas >= totalResponses ? enviadas : totalResponses;
  }

  /** Regla de tres: factor por el que hay que multiplicar un score 0-10 del back para llevarlo a maximoEscala() */
  private factorEscala(institutionId: string): number {
    return this.maximoEscala(institutionId) / ResultadosClimaService.ESCALA_BACK;
  }

  /**
   * Reescala min/max de una lista de semáforos (ya parseados a número) a la escala real
   * de la institución. Antes de escalar, acota cada valor al techo real del back
   * (ESCALA_BACK): el tramo superior de un semáforo a veces se configura con un valor
   * "grande" (ej. 100) para significar "y hacia arriba" sin poner un tope real; escalar
   * ese sentinel tal cual daría un máximo absurdo (100 * 0.5 = 50 en vez de 5).
   */
  public escalarSemaforos<T extends { min: number; max: number }>(semaforos: T[], institutionId: string): T[] {
    const factor = this.factorEscala(institutionId);
    const tope = ResultadosClimaService.ESCALA_BACK;
    return semaforos.map(s => ({
      ...s,
      min: Number((Math.min(s.min, tope) * factor).toFixed(2)),
      max: Number((Math.min(s.max, tope) * factor).toFixed(2)),
    }));
  }

  /** Reescala todos los averageScore de un resultado (raíz, demographics y categories) */
  private escalarResultados(res: AnonymousResults | null, factor: number): AnonymousResults | null {
    if (!res || factor === 1) return res;
    const escalar = (v: number | null | undefined) => v != null ? Number((v * factor).toFixed(2)) : (v ?? null);

    return {
      ...res,
      overallAverageScore: escalar(res.overallAverageScore),
      demographics: (res.demographics ?? []).map(d => ({
        ...d,
        values: d.values.map(v => ({ ...v, averageScore: escalar(v.averageScore) })),
      })),
      categories: (res.categories ?? []).map(c => ({
        ...c,
        averageScore: escalar(c.averageScore),
        demographics: (c.demographics ?? []).map(d => ({
          ...d,
          values: d.values.map(v => ({ ...v, averageScore: escalar(v.averageScore) })),
        })),
      })),
    };
  }

  /** Reescala todos los averageScore de un cruce (por dimensión: celdas y marginales) */
  private escalarCruce(cruce: AnonymousResultsCross | null, factor: number): AnonymousResultsCross | null {
    if (!cruce || factor === 1) return cruce;
    const escalar = (v: number | null | undefined) => v != null ? Number((v * factor).toFixed(2)) : (v ?? null);

    return {
      ...cruce,
      categories: cruce.categories.map(cat => ({
        ...cat,
        overallAverageScore: escalar(cat.overallAverageScore),
        cells: cat.cells.map(c => ({ ...c, averageScore: escalar(c.averageScore) })),
        marginalsA: cat.marginalsA.map(m => ({ ...m, averageScore: escalar(m.averageScore) })),
        marginalsB: cat.marginalsB.map(m => ({ ...m, averageScore: escalar(m.averageScore) })),
      })),
    };
  }

  /** Se lee el token fresco en cada llamada por si rota durante la sesión */
  private get headers(): HttpHeaders {
    return new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('Authorization', 'Bearer ' + localStorage.getItem('token'));
  }

  /**
   * QUERY 1 — periodos de la institución (para el selector).
   * @param institutionId id PLANO de la institución (del allocation, no del token).
   */
  public async getPeriods(institutionId: string): Promise<AnonymousResultPeriod[]> {
    const { data } = await firstValueFrom(
      this.apollo.use(this.apiURL).watchQuery<any>({
        query: getAnonymousResultPeriods,
        context: { headers: this.headers },
        variables: { institutionId: String(institutionId) },
        fetchPolicy: 'no-cache',
      }).valueChanges
    );
    return data?.getAnonymousResultPeriods ?? [];
  }

  /**
   * QUERY 2 — resultados de un periodo.
   * @param allocationId allocation elegido en el selector.
   * @param institutionId id PLANO de la institución; define el factor de la regla de tres.
   * @param filtros recorte demográfico opcional; sin él devuelve la población completa.
   */
  public async getResults(allocationId: number, institutionId: string, filtros: FiltroDemografico[] = []): Promise<AnonymousResults | null> {
    const { data } = await firstValueFrom(
      this.apollo.use(this.apiURL).watchQuery<any>({
        query: getAnonymousResults,
        context: { headers: this.headers },
        variables: {
          allocationId: Number(allocationId),
          filters: filtros.length ? filtros : null,
        },
        fetchPolicy: 'no-cache',
      }).valueChanges
    );
    return this.escalarResultados(data?.getAnonymousResults ?? null, this.factorEscala(institutionId));
  }

  /**
   * QUERY 3 — cruce de dos ejes demográficos por dimensión (tabla combinada).
   * @param allocationId allocation elegido en el selector.
   * @param institutionId id PLANO de la institución; define el factor de la regla de tres.
   * @param keyA eje demográfico de las filas (key exacta del catálogo).
   * @param keyB eje demográfico de las columnas (key exacta del catálogo).
   */
  public async getCross(allocationId: number, institutionId: string, keyA: string, keyB: string): Promise<AnonymousResultsCross | null> {
    const { data } = await firstValueFrom(
      this.apollo.use(this.apiURL).watchQuery<any>({
        query: getAnonymousResultsCross,
        context: { headers: this.headers },
        variables: { allocationId: Number(allocationId), keyA, keyB },
        fetchPolicy: 'no-cache',
      }).valueChanges
    );
    return this.escalarCruce(data?.getAnonymousResultsCross ?? null, this.factorEscala(institutionId));
  }

  /**
   * QUERY 4 — estructura del cuestionario por id (catálogo de dimensiones).
   * @param id id del cuestionario (allocation.questionary.id).
   */
  public async getQuestionnaire(id: number): Promise<Questionnaire | null> {
    const { data } = await firstValueFrom(
      this.apollo.use(this.apiURL).watchQuery<any>({
        query: getQuestionnaireById,
        context: { headers: this.headers },
        variables: { id: Number(id), args: { relations: { loadRelations: 'FULL' } } },
        fetchPolicy: 'no-cache',
      }).valueChanges
    );
    return data?.questionnaireById ?? null;
  }
}
